import { useLayoutEffect, useRef } from 'preact/hooks';
import './Strip.css';

export interface StripProps<Id extends string> {
  items: { id: Id; label: string }[];
  /** Omitted (rather than defaulted to the first item) when NONE of the
   *  chips represent the current screen — Today itself renders the
   *  Materials strip this way: tapping a chip enters the Materials
   *  workshop, but while still on Today none of them is "active". */
  activeId?: Id;
  onSelect: (id: Id) => void;
}

// THE universal secondary-nav pattern (p7-shell-locked): a scrollable,
// gold-highlighted chip strip that sits above the bottom nav for any tab
// with sub-views. Built once, used by four consumers — Auditions (a
// single-select STAGE filter), Projects (a single-select PHASE filter),
// Career (a section switch — Money/Documents/Development/Stats), and
// Today's Materials workshop (a section switch — This is me/Headshots/
// Resume/Reels). No prefix/section label is ever rendered here (locked
// hard rule: no instructional captions anywhere) — the chips speak for
// themselves, exactly as the blessed reference mock's `.subnav` does.
export function Strip<Id extends string>({ items, activeId, onSelect }: StripProps<Id>) {
  // A NEW SET OF CHIPS OPENS AT ITS START. The strip is one element at one
  // place in the tree (the nav zone on a phone), so moving between tabs hands
  // Preact the SAME <div> with a different chip list — and a div keeps its
  // scrollLeft. A strip scrolled on one screen opened scrolled on the next,
  // its first chip cut ("FFERED", the CallToWrap demo walk 2026-09-27).
  // Reset only when the chip SET changes: tapping a chip on the same strip
  // must not yank it back.
  const ref = useRef<HTMLDivElement | null>(null);
  const itemsKey = items.map((i) => i.id).join('\u0000');
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollLeft = 0;
  }, [itemsKey]);
  return (
    <div class="strip" role="tablist" ref={ref}>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === activeId}
          class={`strip__chip${item.id === activeId ? ' strip__chip--active' : ''}`}
          onClick={() => onSelect(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}
